<template>
  <Breadcrumbs title="" />
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12 col-md-10 offset-md-1">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-center" v-if="displayCoverUrl">
              <figure class="col-xl-12 col-sm-12">
                <img style="width: 100%;box-shadow: 0em 0em 1em rgb(0 0 0 / 50%);border-radius: 0.6em;border: none;padding: 0;background: transparent;"
                  :src="displayCoverUrl"
                  alt="Image description"
                  @error="handleCoverImgError"
                />
              </figure>
            </div>
            <h5 v-if="this.eventDetails.title">{{ this.eventDetails.title }}</h5>
            <h5><vue-feather type="tag" size="20"></vue-feather> {{ t('page_title') }}</h5>

            <!-- STEP 1: เลือกประเภทคูปอง -->
            <div class="row" style="margin: 10px 0;" v-if="!createdOrder">
              <div class="col-4" v-for="type in couponTypes" :key="type.key" style="padding: 2px;">
                <button class="btn w-100"
                        :class="[
                          selectedType === type.key ? 'btn-primary' : 'btn-outline-primary',
                          { disabled: !hasCouponsInType(type.key) }
                        ]"
                        :disabled="!hasCouponsInType(type.key)"
                        @click="selectType(type.key)">
                  <template v-if="type.key === 'BOTH'">
                    <vue-feather type="image" size="14" class="me-1"></vue-feather>
                    <span class="me-1">{{ currentLanguage === 'en' ? 'Photo' : 'รูป' }}</span>
                    <span class="me-1">+</span>
                    <vue-feather type="video" size="14" class="me-1"></vue-feather>
                    <span>{{ currentLanguage === 'en' ? 'Video' : 'วิดีโอ' }}</span>
                  </template>
                  <template v-else>
                    <vue-feather :type="getCouponTypeIcon(type.key)" size="14" class="me-1"></vue-feather>
                    {{ type.label }}
                  </template>
                </button>
              </div>
            </div>

            <!-- STEP 2: กล่องรายละเอียดคูปอง (จาก /v1/coupon/list) -->
            <div v-if="!createdOrder">
              <div v-if="loadingCoupons" class="text-center" style="padding: 20px;">
                {{ t('loading') }}
              </div>
              <div v-else-if="filteredCoupons.length === 0" class="text-center" style="padding: 20px;">
                {{ t('no_coupon') }}
              </div>
              <div v-for="coupon in filteredCoupons" :key="coupon.coupon_id"
                   class="coupon-box"
                   :class="{ selected: selectedCoupon && selectedCoupon.coupon_id === coupon.coupon_id }"
                   @click="selectedCoupon = coupon">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <b>{{ coupon.title }}</b>
                    <p style="font-size: small; margin: 0;">{{ coupon.description }}</p>
                    <p style="font-size: small; margin: 0;" v-if="coupon.expire_date_text">
                      {{ t('valid_until') }} {{ coupon.expire_date_text }}
                    </p>
                  </div>
                  <div class="text-end">
                    <div class="coupon-price-wrap">
                      <small
                        class="coupon-price-original"
                        v-if="getOriginalPrice(coupon) > toNumber(coupon.price)"
                      >
                        {{ formatInteger(getOriginalPrice(coupon)) }} {{ coupon.payment_currency || 'THB' }}
                      </small>
                      <h5 style="color: #0d6efd; margin: 0; white-space: nowrap;">
                        {{ formatCurrency(coupon.price) }} {{ coupon.payment_currency || 'THB' }}
                      </h5>
                    </div>
                    <span class="badge bg-danger">-{{ getDiscountPercent(coupon) }}%</span>
                  </div>
                </div>
              </div>

              <!-- STEP 3: ฟอร์มข้อมูลผู้ซื้อ -->
              <div v-if="selectedCoupon" style="margin-top: 15px;">
                <label class="form-label">{{ t('name') }}<span class="required-star">*</span></label>
                <input v-model.trim="form.name" type="text" minlength="5" class="form-control" @input="clearFormMessage" />

                <label class="form-label" style="margin-top: 8px;">{{ t('mobile') }}<span class="required-star">*</span></label>
                <input
                  v-model.trim="form.mobile"
                  type="tel"
                  maxlength="10"
                  pattern="[0-9]{10}"
                  inputmode="numeric"
                  class="form-control"
                  @input="onMobileInput"
                />

                <label class="form-label" style="margin-top: 8px;">{{ t('email') }}</label>
                <input v-model.trim="form.email" type="email" class="form-control" @input="clearFormMessage" />
                <small class="form-text text-muted">{{ t('email_hint') }}</small><br>

                <label class="form-label" style="margin-top: 8px;">{{ t('payment_method') }}</label>
                <div style="margin-top: 4px;">
                  <div class="form-check payment-method-option">
                    <input class="form-check-input" type="radio" v-model="paymentMethod" value="PP" id="paymentMethodPP">
                    <label class="form-check-label" for="paymentMethodPP">
                      <b>{{ t('payment_method_promptpay') }}</b>
                      <img class="payment-method-icon" src="../../assets/images/running/promptpay-icon.jpg" />
                    </label>
                  </div>
                  <div class="form-check payment-method-option">
                    <input class="form-check-input" type="radio" v-model="paymentMethod" value="CC" id="paymentMethodCC">
                    <label class="form-check-label" for="paymentMethodCC">
                      <b>{{ t('payment_method_creditcard') }}</b>
                      <img class="payment-method-icon" src="../../assets/images/running/credit-card-icon.png" />
                    </label>
                  </div>
                </div>

                <div v-if="formMessage" class="form-inline-message">{{ formMessage }}</div>

                <button class="btn btn-lg btn-primary w-100" style="margin-top: 15px;"
                        :disabled="isCreating"
                        @click="createCoupon">
                  {{ isCreating ? t('creating') : t('create_qr') }}
                </button>
              </div>
            </div>

            <!-- STEP 4: แสดง QR Code + ข้อมูลออเดอร์ -->
            <div v-if="createdOrder" class="text-center">
              <h5>{{ t('scan_to_pay') }}</h5>
              <div class="d-flex justify-content-center" v-if="createdOrder.qrcode_url">
                <div v-if="!qrImageLoaded" class="qr-placeholder">
                  <div class="spinner-border text-primary" role="status" aria-label="Loading QR code"></div>
                  <div class="qr-placeholder-text">Loading QR code...</div>
                </div>
                <img
                  :src="createdOrder.qrcode_url"
                  alt="Payment QR Code"
                  style="width: 250px; max-width: 100%; height: auto; border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,0.08);"
                  @load="handleQrLoaded"
                  @error="handleQrError"
                  v-show="qrImageLoaded"
                />
              </div>

              <p style="margin-top: 10px;">
                <b>OrderNo:</b> {{ createdOrder.uuid || createdOrder.order_no }}<br />
                <b>{{ t('name') }}:</b> {{ form.name }}<br />
                <b>{{ t('mobile') }}:</b> {{ form.mobile }}<br />
                <b>{{ t('email') }}:</b> {{ createdOrder.email ? createdOrder.email : '-' }}
              </p>
              <div class="payment-summary" v-if="createdOrder.amount !== undefined || createdOrder.paid_amount !== undefined">
                <div class="summary-row">
                  <span>{{ t('coupon_price_amount') }}</span>
                  <b>{{ formatCurrency(createdOrder.amount) }} {{ createdOrder.payment_currency || 'THB' }}</b>
                </div>
                <div class="summary-row" v-if="getPaymentFee() > 0">
                  <span>{{ t('payment_fee') }}</span>
                  <b>{{ formatCurrency(getPaymentFee()) }} {{ createdOrder.payment_currency || 'THB' }}</b>
                </div>
                <div class="summary-row total" v-if="createdOrder.paid_amount !== undefined">
                  <span>{{ t('paid_amount_total') }}</span>
                  <b>{{ formatCurrency(createdOrder.paid_amount) }} {{ createdOrder.payment_currency || 'THB' }}</b>
                </div>
              </div>
              <p style="margin-top: 8px;" v-if="createdOrder.create_date">
                <b>{{ t('create_date') }}:</b> {{ createdOrder.create_date }}
              </p>
              <p style="font-size: small; color: #6c757d;">{{ t('capture_hint') }}</p>
            </div>

          </div>
        </div>

        <div class="card" style="margin-top: 14px;">
          <div class="card-body" style="padding-bottom: 10px;">
            <div class="d-flex justify-content-between align-items-center" style="gap: 8px;">
              <h6 style="margin: 0;">
                <vue-feather type="search" size="16"></vue-feather>
                {{ t('paid_coupon_search_title') }}
              </h6>
              <button class="btn btn-outline-primary btn-sm" @click="toggleCouponSearchPanel">
                <vue-feather :type="showCouponSearchPanel ? 'chevron-up' : 'chevron-down'" size="14" class="me-1"></vue-feather>
                {{ showCouponSearchPanel ? t('hide_search_detail') : t('show_search_detail') }}
              </button>
            </div>
          </div>

          <div class="card-body" v-if="showCouponSearchPanel" style="padding-top: 0;">
            <p style="font-size: 0.9rem; color: #6c757d; margin-bottom: 10px;">
              {{ t('paid_coupon_search_subtitle') }}
            </p>

            <div class="form-check form-check-inline" style="margin-bottom: 8px;">
              <input
                class="form-check-input"
                v-model="couponSearchType"
                type="radio"
                id="couponSearchTypeReceipt"
                value="receipt"
                @change="onCouponSearchTypeChange"
              >
              <label class="form-check-label" for="couponSearchTypeReceipt">
                {{ t('search_by_receipt') }}
              </label>
            </div>
            <div class="form-check form-check-inline" style="margin-bottom: 8px;">
              <input
                class="form-check-input"
                v-model="couponSearchType"
                type="radio"
                id="couponSearchTypeMobile"
                value="mobile"
                @change="onCouponSearchTypeChange"
              >
              <label class="form-check-label" for="couponSearchTypeMobile">
                {{ t('search_by_mobile') }}
              </label>
            </div>

            <form @submit.prevent="searchPaidCouponsByReceipt">
              <div class="row g-2 align-items-end">
                <div class="col-sm-8">
                  <label class="form-label">{{ couponSearchType === 'receipt' ? t('receipt_ref_label') : t('mobile_search_label') }}</label>
                  <input
                    v-model.trim="couponReceiptRef"
                    type="text"
                    class="form-control"
                    :minlength="couponSearchType === 'receipt' ? 8 : 10"
                    :maxlength="couponSearchType === 'receipt' ? 8 : 10"
                    :pattern="couponSearchType === 'receipt' ? '[A-Za-z0-9]{8}' : '[0-9]{10}'"
                    :inputmode="couponSearchType === 'receipt' ? 'text' : 'numeric'"
                    :placeholder="couponSearchType === 'receipt' ? t('receipt_ref_placeholder') : t('mobile_search_placeholder')"
                    @input="onCouponSearchInput"
                    required
                  />
                </div>
                <div class="col-sm-4 d-grid">
                  <button type="submit" class="btn btn-primary" :disabled="isCouponSearching">
                    <vue-feather type="loader" size="14" animation="spin" v-if="isCouponSearching"></vue-feather>
                    <vue-feather type="search" size="14" class="me-1" v-else></vue-feather>
                    {{ isCouponSearching ? t('searching') : t('search_paid_coupon') }}
                  </button>
                </div>
              </div>
            </form>

            <div v-if="couponSearchType === 'receipt'" style="margin-top: 8px;">
              <b style="font-size: 0.85rem;">{{ t('receipt_example_note') }}</b>
              <div class="d-flex justify-content-left" style="margin-top: 6px;">
                <img width="320" src="../../assets/images/running/example-slip.jpg" alt="Receipt example" />
              </div>
            </div>

            <div v-if="couponSearchMessage" class="form-inline-message" style="margin-top: 8px; margin-bottom: 0;">
              {{ couponSearchMessage }}
            </div>

            <div v-if="couponSearchStarted" style="margin-top: 12px;">
              <div class="table-responsive" v-if="paidCouponResults.length">
                <table class="table table-striped table-sm" style="margin-bottom: 0;">
                  <thead>
                    <tr>
                      <th>{{ t('status_col') }}</th>
                      <th>{{ t('order_ref') }}</th>
                      <th>{{ t('amount_col') }}</th>
                      <th>{{ t('order_date') }}</th>
                      <th>{{ t('payment_date_col') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in paidCouponResults" :key="index">
                      <td>
                        <span
                          class="badge"
                          :class="item.status === 'PAID' ? 'bg-success' : (item.status === 'INITIAL' ? 'bg-warning text-dark' : 'bg-secondary')"
                        >
                          {{ item.status || '-' }}
                        </span>
                      </td>
                      <td>
                        <u><a v-if="item.uuid" :href="`/coupon/${item.uuid}`" target="_blank">
                          {{ item.uuid }}
                        </a>
                        <span v-else>{{ item.order_no || item.order_id || '-' }}</span></u>
                      </td>
                      <td>{{ formatCurrency(item.amount) }} {{ item.payment_currency || 'THB' }}</td>
                      <td>{{ item.create_date || '-' }}</td>
                      <td>{{ item.payment_date || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center" style="padding: 8px 0; color: #6c757d;">
                {{ t('no_paid_coupon_found') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import router from "../../router";

export default {
  data() {
    return {
      currentLanguage: 'th',
      eventDetails: {},
      loadingCoupons: false,
      isCreating: false,
      coupons: [],
      selectedType: 'BOTH', // default = PHOTO+VIDEO
      selectedCoupon: null,
      createdOrder: null,
      qrImageLoaded: false,
      paymentMethod: 'PP',
      formMessage: '',
      couponSearchMessage: '',
      showCouponSearchPanel: false,
      couponSearchType: 'receipt',
      couponReceiptRef: '',
      couponSearchStarted: false,
      isCouponSearching: false,
      paidCouponResults: [],
      coverImgFailed: false,
      form: { name: '', mobile: '', email: '' },
      dict: {
        page_title: { en: 'Buy special price coupon', th: 'ซื้อคูปองราคาพิเศษ' },
        loading: { en: 'Loading...', th: 'กำลังโหลดข้อมูล...' },
        no_coupon: { en: 'No coupon available for this period', th: 'ไม่มีคูปองจำหน่ายในช่วงเวลานี้' },
        valid_until: { en: 'Available for purchase from today until', th: 'สามารถซื้อได้ตั้งแต่วันนี้ - ' },
        name: { en: 'Name', th: 'ชื่อ' },
        mobile: { en: 'Mobile number', th: 'หมายเลขโทรศัพท์' },
        email: { en: 'Email (optional)', th: 'อีเมล (ถ้ามี)' },
        email_hint: { en: 'The system will automatically send an email to this address. Please also check your Spam/Junk mail folder.', th: 'ระบบจะส่งอีเมลให้อัตโนมัติ กรุณาตรวจสอบในกล่อง Spam หรือ Junk mail ด้วย' },
        create_qr: { en: 'Create QR Code for payment', th: 'สร้าง QR Code สำหรับชำระเงิน' },
        payment_method: { en: 'Payment method', th: 'ช่องทางการชำระเงิน' },
        payment_method_promptpay: { en: 'PromptPay QR', th: 'พร้อมเพย์ (QR Code)' },
        payment_method_creditcard: { en: 'Credit / Debit Card', th: 'บัตรเครดิต/เดบิต' },
        pay_now: { en: 'PAY NOW', th: 'PAY NOW' },
        creating: { en: 'Creating...', th: 'กำลังสร้างรายการ...' },
        scan_to_pay: { en: 'Scan QR Code to pay', th: 'สแกน QR Code เพื่อชำระเงิน' },
        capture_hint: { en: 'Please capture or save this QR code to pay', th: 'กรุณาแคปหน้าจอหรือบันทึก QR Code เพื่อนำไปสแกนจ่าย' },
        coupon_price_amount: { en: 'Coupon price', th: 'ราคาคูปอง' },
        payment_fee: { en: 'VAT (7%)', th: 'ภาษีมูลค่าเพิ่ม (VAT 7%)' },
        paid_amount_total: { en: 'Total to pay', th: 'รวมยอด' },
        create_date: { en: 'Create date', th: 'วันที่สร้างรายการ' },
        fill_required: { en: 'Please enter name and mobile number', th: 'กรุณากรอกชื่อและหมายเลขโทรศัพท์' },
        name_min_length: { en: 'Name must be at least 5 characters', th: 'ชื่อต้องมีอย่างน้อย 5 ตัวอักษร' },
        mobile_invalid: { en: 'Mobile number must be exactly 10 digits', th: 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลักเท่านั้น' },
        email_invalid: { en: 'Please enter a valid email format', th: 'รูปแบบอีเมลไม่ถูกต้อง' },
        invalid_amount: { en: 'Invalid coupon amount, please try another coupon', th: 'จำนวนเงินคูปองไม่ถูกต้อง กรุณาเลือกคูปองใหม่อีกครั้ง' },
        create_failed: { en: 'Failed to create order, please try again', th: 'สร้างรายการไม่สำเร็จ กรุณาลองอีกครั้ง' },
        paid_coupon_search_title: { en: 'Find paid coupon order', th: 'ค้นหาคูปองที่ชำระเงินแล้ว' },
        paid_coupon_search_subtitle: { en: 'Search by payment slip reference (last 8 chars) or mobile number', th: 'ค้นหาด้วยเลขอ้างอิงสลิป 8 ตัวท้าย หรือ เบอร์มือถือ' },
        show_search_detail: { en: 'Show search form', th: 'เปิดฟอร์มค้นหา' },
        hide_search_detail: { en: 'Hide search form', th: 'ซ่อนฟอร์มค้นหา' },
        search_by_receipt: { en: 'Receipt', th: 'สลิปโอนเงิน' },
        search_by_mobile: { en: 'Mobile', th: 'เบอร์มือถือ' },
        receipt_ref_label: { en: 'Payment slip reference', th: 'เลขอ้างอิงสลิปโอนเงิน' },
        receipt_ref_placeholder: { en: 'Example: A1B2C3D4', th: 'ตัวอย่าง: A1B2C3D4' },
        receipt_example_note: { en: 'Example: last 8 characters of receipt reference', th: 'ตัวอย่างเลขท้ายสลิป 8 ตัวท้าย' },
        mobile_search_label: { en: 'Mobile number', th: 'หมายเลขโทรศัพท์' },
        mobile_search_placeholder: { en: 'Example: 0812345678', th: 'ตัวอย่าง: 0812345678' },
        search_paid_coupon: { en: 'Search paid coupon', th: 'ค้นหาคูปองที่ชำระแล้ว' },
        searching: { en: 'Searching...', th: 'กำลังค้นหา...' },
        receipt_ref_invalid: { en: 'Please enter 8 English letters/numbers of receipt reference', th: 'กรุณากรอกเลขอ้างอิงสลิป 8 ตัวท้าย โดยใช้เฉพาะตัวอักษรอังกฤษและตัวเลข' },
        mobile_search_invalid: { en: 'Please enter 10-digit mobile number', th: 'กรุณากรอกเบอร์มือถือ 10 หลัก' },
        paid_coupon_search_failed: { en: 'Unable to search paid coupon now, please try again', th: 'ไม่สามารถค้นหาคูปองได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง' },
        no_paid_coupon_found: { en: 'No paid coupon order found', th: 'ไม่พบรายการคูปองที่ชำระเงินแล้ว' },
        order_date: { en: 'Order date', th: 'วันที่สั่งซื้อ' },
        order_ref: { en: 'Order reference', th: 'เลขที่คำสั่งซื้อ' },
        uuid_col: { en: 'UUID / Order link', th: 'UUID / ลิงก์ออเดอร์' },
        mobile_col: { en: 'Mobile', th: 'เบอร์มือถือ' },
        payment_ref_col: { en: 'Payment ref', th: 'เลขอ้างอิงสลิป' },
        amount_col: { en: 'Amount', th: 'ยอดคูปอง' },
        payment_date_col: { en: 'Payment date', th: 'วันที่ชำระเงิน' },
        coupon_code_col: { en: 'Coupon code', th: 'รหัสคูปอง' },
        status_col: { en: 'Status', th: 'สถานะ' },
        paid_status: { en: 'Paid', th: 'ชำระแล้ว' },
      },
    };
  },
  computed: {
    couponCoverUrl() {
      const coverUrl = this.eventDetails?.coverurl || '';
      return coverUrl ? coverUrl.replace(/\/cover\.jpg$/i, '/cover_coupon.jpg') : '';
    },
    displayCoverUrl() {
      const originalCoverUrl = this.eventDetails?.coverurl || '';
      if (this.coverImgFailed) {
        return originalCoverUrl;
      }
      return this.couponCoverUrl || originalCoverUrl;
    },
    couponTypes() {
      return [
        { key: 'BOTH',  label: this.currentLanguage === 'en' ? 'PHOTO+VIDEO' : 'รูป+วิดีโอ' },
        { key: 'PHOTO', label: this.currentLanguage === 'en' ? 'PHOTO' : 'รูปภาพ' },
        { key: 'VIDEO', label: this.currentLanguage === 'en' ? 'VIDEO' : 'วิดีโอ' },
      ];
    },
    filteredCoupons() {
      return this.coupons.filter(c => c.coupon_type === this.selectedType);
    },
  },
  async created() {
    if (localStorage.getItem('currentLanguage') !== null) {
      this.currentLanguage = localStorage.getItem('currentLanguage');
    }
  },
  async mounted() {
    this.getEventDetail(this.$route.params.event_id);
    await this.loadCoupons();
  },
  methods: {
    t(key) {
      const item = this.dict[key];
      return this.currentLanguage === 'en' ? item.en : item.th;
    },
    selectType(key) {
      if (!this.hasCouponsInType(key)) {
        return;
      }
      this.selectedType = key;
      this.selectedCoupon = null;
    },
    hasCouponsInType(typeKey) {
      return this.coupons.some(c => c && c.coupon_type === typeKey);
    },
    ensureSelectedTypeAvailable() {
      if (this.hasCouponsInType(this.selectedType)) {
        return;
      }
      const firstAvailableType = this.couponTypes.find(type => this.hasCouponsInType(type.key));
      this.selectedType = firstAvailableType ? firstAvailableType.key : 'BOTH';
      this.selectedCoupon = null;
    },
    getCouponTypeIcon(typeKey) {
      if (typeKey === 'PHOTO') return 'image';
      if (typeKey === 'VIDEO') return 'video';
      return 'layers';
    },
    toNumber(value) {
      const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value;
      const n = Number(normalized);
      return Number.isFinite(n) ? n : 0;
    },
    getDiscountPercent(coupon) {
      const percent = this.toNumber(coupon?.discount_percent);
      if (percent > 0) {
        return percent;
      }
      return 0;
    },
    getOriginalPrice(coupon) {
      const salePrice = this.toNumber(coupon?.price);
      const discountPercent = this.getDiscountPercent(coupon);
      if (salePrice <= 0 || discountPercent <= 0 || discountPercent >= 100) {
        return salePrice;
      }
      return salePrice / (1 - discountPercent / 100);
    },
    formatCurrency(value) {
      const n = this.toNumber(value);
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatInteger(value) {
      const n = this.toNumber(value);
      return Math.round(n).toLocaleString(undefined);
    },
    getPaymentFee() {
      const paid = this.toNumber(this.createdOrder?.paid_amount);
      const amount = this.toNumber(this.createdOrder?.amount);
      const fee = paid - amount;
      return fee > 0 ? fee : 0;
    },
    handleQrLoaded() {
      this.qrImageLoaded = true;
    },
    handleQrError() {
      this.qrImageLoaded = true;
    },
    clearFormMessage() {
      this.formMessage = '';
    },
    handleCoverImgError() {
      if (!this.coverImgFailed) {
        this.coverImgFailed = true;
      }
    },
    onMobileInput(event) {
      const onlyDigits = (event.target.value || '').replace(/\D/g, '').slice(0, 10);
      this.form.mobile = onlyDigits;
      this.clearFormMessage();
    },
    onCouponSearchInput(event) {
      let normalized = event.target.value || '';
      if (this.couponSearchType === 'receipt') {
        normalized = normalized.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 8);
      } else {
        normalized = normalized.replace(/\D/g, '').slice(0, 10);
      }
      this.couponReceiptRef = normalized;
      this.couponSearchMessage = '';
    },
    onCouponSearchTypeChange() {
      this.couponReceiptRef = '';
      this.couponSearchMessage = '';
      this.couponSearchStarted = false;
      this.paidCouponResults = [];
    },
    toggleCouponSearchPanel() {
      this.showCouponSearchPanel = !this.showCouponSearchPanel;
    },
    async getEventDetail(event_key) {
      try {
        const res = await axios.get("/v1/events/" + event_key);
        if (res.data.status === "success") {
          this.eventDetails = res.data;
        } else {
          router.push({ path: "/404" });
        }
      } catch (e) {
        router.push({ path: "/404" });
      }
    },
    async loadCoupons() {
      this.loadingCoupons = true;
      try {
        const res = await axios.post("/v1/coupon/list", {
          event_key: this.$route.params.event_id,
        });
        if (res.data.status === 'success') {
          this.coupons = res.data.body || [];
        }
      } catch (e) {
        this.coupons = [];
      }
      this.ensureSelectedTypeAvailable();
      if (this.selectedCoupon && !this.filteredCoupons.some(c => c.coupon_id === this.selectedCoupon.coupon_id)) {
        this.selectedCoupon = null;
      }
      this.loadingCoupons = false;
    },
    async createCoupon() {
      this.formMessage = '';

      const name = (this.form.name || '').trim();
      const mobile = (this.form.mobile || '').trim();
      const email = (this.form.email || '').trim();

      if (!name || !mobile) {
        this.formMessage = this.t('fill_required');
        return;
      }

      if (name.length < 5) {
        this.formMessage = this.t('name_min_length');
        return;
      }

      if (!/^\d{10}$/.test(mobile)) {
        this.formMessage = this.t('mobile_invalid');
        return;
      }

      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.formMessage = this.t('email_invalid');
        return;
      }

      const selectedPriceRaw = this.selectedCoupon?.price;
      const selectedPriceNumeric = Number(String(selectedPriceRaw).replace(/,/g, ''));
      if (!Number.isFinite(selectedPriceNumeric) || selectedPriceNumeric <= 0) {
        this.formMessage = this.t('invalid_amount');
        return;
      }

      this.isCreating = true;
      try {
        const res = await axios.post("/v1/coupon/create", {
          event_key: this.$route.params.event_id,
          coupon_id: this.selectedCoupon.coupon_id,
          coupon_type: this.selectedType,
          // Send coupon sale price as-is from selected coupon (no discount math on frontend)
          price: selectedPriceRaw,
          amount: selectedPriceRaw,
          coupon_price: selectedPriceRaw,
          name: name,
          mobile: mobile,
          email: email,
          payment_channel: this.paymentMethod,
        });
        if (res.data.status === 'success') {
          const createdUuid = res.data.uuid || res.data.order_no;
          this.qrImageLoaded = false;
          this.$router.push(`/coupon/${createdUuid}`);
        } else {
          this.formMessage = this.t('create_failed');
        }
      } catch (e) {
        this.formMessage = this.t('create_failed');
      }
      this.isCreating = false;
    },
    async searchPaidCouponsByReceipt() {
      this.couponSearchMessage = '';
      this.couponSearchStarted = true;
      this.paidCouponResults = [];

      const searchValueRaw = (this.couponReceiptRef || '').trim();
      let filterType = 'payment_ref';
      let searchValue = searchValueRaw.toUpperCase();

      if (this.couponSearchType === 'receipt') {
        if (!/^[A-Z0-9]{8}$/.test(searchValue)) {
          this.couponSearchMessage = this.t('receipt_ref_invalid');
          return;
        }
      } else {
        filterType = 'mobile';
        searchValue = searchValueRaw.replace(/\D/g, '');
        if (!/^\d{10}$/.test(searchValue)) {
          this.couponSearchMessage = this.t('mobile_search_invalid');
          return;
        }
      }

      this.isCouponSearching = true;
      try {
        const payload = {
          tk: process.env.VUE_APP_FN_TO_BK_SECRET_KEY,
          filter_type: filterType,
          txt_search: searchValue,
          event_key: this.$route.params.event_id,
        };
        const res = await axios.post('/v1/coupon/find-order', payload);
        const body = Array.isArray(res.data?.body) ? res.data.body : [];
        this.paidCouponResults = body;
      } catch (e) {
        this.couponSearchMessage = this.t('paid_coupon_search_failed');
      }
      this.isCouponSearching = false;
    },
  },
};
</script>

<style scoped>
.required-star {
  color: #dc3545;
  margin-left: 2px;
}
.payment-method-option {
  margin-bottom: 8px;
}
.payment-method-icon {
  width: 60px;
  margin-left: 8px;
  vertical-align: middle;
}
.coupon-box {
  border: 1px solid #cfe0ff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.coupon-box:hover {
  box-shadow: 0 4px 10px rgba(13, 110, 253, 0.2);
}
.coupon-box.selected {
  border: 2px solid #0d6efd;
  background: linear-gradient(135deg, #eef5ff, #e0ecff);
}

.coupon-price-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
}

.coupon-price-original {
  color: #6c757d;
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
  margin-bottom: 2px;
}

.form-inline-message {
  margin-top: 10px;
  margin-bottom: -4px;
  font-size: 0.85rem;
  color: #dc3545;
}

.payment-summary {
  margin-top: 8px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px;
  text-align: left;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.95rem;
  margin-bottom: 4px;
}

.summary-row.total {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #ced4da;
}

.qr-placeholder {
  width: 250px;
  max-width: 100%;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}

.qr-placeholder-text {
  font-size: 0.9rem;
  color: #6c757d;
}

</style>
